import { useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { InvoiceStatus } from '@/shared/types';
import { ConditionView } from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { useShortPolling } from '@/hooks/useShortPolling';

import {
  servicePaymentDataSchema,
  ServicePaymentMergedSchema,
} from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/model/schemas';
import {
  ServicePaymentDataProps,
  ServiceProps,
} from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/model/types';
import { Payment } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/ui/steps/Payment';
import { ServicePaymentData } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/ui/steps/ServicePaymentData';
import { ServicePaymentPreview } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/ui/steps/ServicePaymentPreview';
import { ServicePaymentResult } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/ui/steps/ServicePaymentResult';

import AuthContext from '@/contexts/Auth/AuthContext';
import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { getServicesForPaymentSelect } from '@/api/apiService';
import { PaymentServiceResponseOption, FormDataType, UserServicePaymentType } from '@/api/types';

import api from '@/api';

import { localStorageController } from '../../../../../Cache';

import { SERVICE_CODE, STEP_NUMBER_TO_PAY, STEP_NUMBER_TO_RESULT } from './lib/constants';
import { generatePayload } from './lib/generatePayload';

import './ServicePaymentWithoutApplication.scss';

const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Дані для оплати',
    step: ServicePaymentData,
    validationSchema: servicePaymentDataSchema,
  },
  {
    title: 'Перевірка даних',
    step: ServicePaymentPreview,
  },
  { title: 'Оплата', step: Payment },
  { title: 'Результат', step: ServicePaymentResult },
];

export const ServicePaymentWithoutApplication: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const { isLoggedIn } = useContext(AuthContext);
  const operId = useMemo<Nullable<number>>(() => {
    const cachedData = localStorageController.cachedData;
    const stringifiedOperId = cachedData?.['/pay01/oper_id']?.data;
    return stringifiedOperId ? Number(stringifiedOperId) : null;
  }, []);
  const [isPymentServiceAvailable, setIsPaymentServiceAvailable] = useState<boolean>(true);

  const currentService = getServiceByCode(SERVICE_CODE);

  const {
    isShortPollingSuccessed,
    isShortPollingInProgress,
    isShortPollingFailed,
    startShortPolling,
    onResetPollingData,
  } = useShortPolling({
    delayTime: 1000 * 15, // 15sec
    totalAwaitTime: 1000 * 120, // 120sec
    async fetchFunction() {
      if (operId) {
        const { status } = await api.pay.services.getPaymentDetails(operId);

        return status;
      }
    },
    isResponseAppropriate(status) {
      return status === InvoiceStatus.SUCCESS;
    },
    isResponseUnAppropriate(status) {
      return status === InvoiceStatus.FAILED;
    },
  });

  const [defaultValues] = useState<FormDataType>({});

  const onGercPaymentContinue = async (): Promise<number> => {
    if (!isLoggedIn) return STEP_NUMBER_TO_RESULT;

    startShortPolling();

    return STEP_NUMBER_TO_PAY;
  };

  const generatePaymentDetailsInfo = (
    formData: ServicePaymentMergedSchema,
  ): ReturnType<typeof api.pay.services.paymentInfo> => api.pay.services.paymentInfo(generatePayload({ formData }));

  const checkDefraymentServiceAvailability = async (formData: ServicePaymentDataProps): Promise<boolean> => {
    const currentDefraymentService: Nullable<PaymentServiceResponseOption> = formData.defraymentService
      ? formData.defraymentService
      : null;
    let isDefraymentServiceAvailable: boolean = true;

    if (currentDefraymentService) {
      const currentDefraymentSubService =
        currentDefraymentService.data.length > 1
          ? formData.defraymentSubService
            ? formData.defraymentSubService
            : null
          : currentDefraymentService.data[0];

      if (currentDefraymentSubService) {
        await getServicesForPaymentSelect((services) => {
          const updatedDefraymentService = services.find((service) => service.id === currentDefraymentService.id);
          isDefraymentServiceAvailable = updatedDefraymentService
            ? updatedDefraymentService.data.some((subService) => subService.id === currentDefraymentSubService.id)
            : false;
        });
      }
    }

    setIsPaymentServiceAvailable(isDefraymentServiceAvailable);
    return isDefraymentServiceAvailable;
  };

  const finalInvoiceStatus = useMemo(() => {
    if (!isLoggedIn) {
      return InvoiceStatus.SUCCESS;
    }

    if (isShortPollingSuccessed) {
      return InvoiceStatus.SUCCESS;
    }
    if (isShortPollingFailed) {
      return InvoiceStatus.FAILED;
    }
  }, [isLoggedIn, isShortPollingSuccessed, isShortPollingFailed]);

  return (
    <ConditionView if={!!currentService} else={<Loader />}>
      <ServiceFormTemplate
        className="service-payment-without-application-service"
        serviceCode={SERVICE_CODE}
        title={currentService?.name ?? ''}
        steps={STEPS}
        defaultValues={defaultValues}
        currentRequestId={null}
        onGercPaymentContinue={onGercPaymentContinue}
        stepProps={{
          checkDefraymentServiceAvailability,
          setIsPaymentServiceAvailable,
          invoiceStatus: finalInvoiceStatus,
          paymentShortPolling: { isShortPollingInProgress, onResetPollingData },
          finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
          allowedPaymentType: currentService?.paymentType as UserServicePaymentType,
          isPymentServiceAvailable,
          generatePaymentDetailsInfo,
        }}
      />
    </ConditionView>
  );
};
